import {
  addMonths,
  endOfMonth,
  isAfter,
  isBefore,
  parseISO,
  startOfDay,
  startOfMonth,
} from "date-fns";
import {
  MIN_RECURRING_COUNT,
  RecurrenceInput,
  RecurrenceRule,
} from "./recurrence";
import { parseRepeatParam } from "@/offering_v2/utils/repeat_query_param";
import { useCallback, useEffect, useState } from "react";

import {
  AvailableDay,
  OrderItemMembershipInput,
  useGetSpaceAvailableDays,
  useGetSpaceAvailableTimeSlots,
} from "../offering/api";
import { TemplateName } from "../data/partner_offering";
import { isValidDay, toDay, toMonthWindow } from "../lib/datetime";
import { useI18n } from "../lib/i18n/i18n_provider";
import {
  AddOnType,
  BookingType,
  SpaceOffering,
  OrderableType,
  OrderItemInput,
  OrderSource,
  QuoteInput,
  useMultiPassAvailabilityQuery,
} from "./api";
import { addDays } from "./date_utils";
import { Day } from "./day_utils";
import { AddOnsQueryParams, OfferingPageQueryParams } from "./offering";
import { Time } from "./time_utils";
import {
  DateTimeValue,
  isHourlyValue,
} from "@/offering_v2/components/date-time-picker/date_time_picker";
import { getNonConsecutiveDates } from "./format_date_range";
import { useQueryString } from "@/hooks";

/**
 * Number of days between start date and end date params sent to available days endpoint.
 * Desktop: available days are fetched for the only 2 months currently visible in the calendar picker
 * Mobile: available days are fetched for as many months currently visible in the calendar picker (capped at 180 days)
 */
const NUMBER_OF_DAYS_CHECK_FOR_AVAILABILITY = 180;

export function isDateBlockedByAvailableDays({
  availableDays,
  date,
  requireFullDay = false,
}: {
  availableDays?: AvailableDay[];
  date: Date;
  requireFullDay?: boolean;
}) {
  const dateAvailability = availableDays?.find(
    (day) => day.date === toDay(date)
  );
  if (!dateAvailability) {
    return true;
  }

  if (requireFullDay && dateAvailability.availableFullDay === false) {
    return true;
  }

  return false;
}

export interface AddOnOrderParams {
  spaceOfferingID: string;
  quantity?: number;
  date: string;
}

export interface DayPassOrderParams {
  type: TemplateName.DayPass;
  spaceID?: string;
  quantity?: number;
  capacity?: number;
  dateRange?: DayPassDateRange;
  promoCode?: string;
  email?: string;
  attendees?: number;
  addOns?: AddOnOrderParams[];
}

export interface DayPassWithCapacityOrderParams {
  type: "DayPassWithCapacity";
  spaceID?: string;
  partnerOfferingLocationID?: string;
  capacity?: number;
  dateRange?: DayPassDateRange;
  promoCode?: string;
  email?: string;
  attendees?: number;
}

export interface DailyOfficeOrderParams {
  type: "DailyOffice";
  spaceID?: string;
  addOnID?: string;
  spaceOfferingID?: string;
  partnerOfferingLocationID?: string;
  capacity?: number;
  // Hourly-priced day offices carry { date, startTime, endTime }; daily-only
  // ones carry { startDate, endDate }. Discriminate with `isHourlyValue`.
  dateRange?: DateTimeValue;
  promoCode?: string;
  email?: string;
  city?: string;
  attendees?: number;
  addOns?: AddOnOrderParams[];
}

export interface DailyAccessOrderParams {
  type: "DailyAccess";
  spaceID?: string;
  addOnID?: string;
  spaceOfferingID?: string;
  // Hourly-priced day passes carry { date, startTime, endTime }; daily-only ones
  // carry { startDate, endDate }. Discriminate with `isHourlyValue` (mirrors
  // DailyOfficeOrderParams). Quantity applies to both modes.
  dateRange?: DateTimeValue;
  promoCode?: string;
  quantity?: number;
  email?: string;
  city?: string;
  attendees?: number;
  addOns?: AddOnOrderParams[];
}

export interface MeetingRoomOrderParams {
  type: "MeetingRoom";
  spaceID?: string;
  quantity?: number;
  dateRange?: DateTimeValue;
  promoCode?: string;
  email?: string;
  attendees?: number;
  layoutID?: string;
  addOns?: AddOnOrderParams[];
  // Set when the customer repeats the booking. `dateRange` is the anchor (the
  // first date and, for hourly, the window every date shares); the dates
  // themselves are re-derived from the availability horizon, never stored.
  recurrence?: RecurrenceRule;
}

export interface EventSpaceOrderParams {
  type: "EventSpace";
  spaceID?: string;
  promoCode?: string;
  email?: string;
  dateRange?: DateTimeValue;
  attendees?: number;
  eventTypeID?: string;
  eventTypeDescription?: string;
  layoutID?: string;
  addOns?: AddOnOrderParams[];
}

export interface DayOfficeOrderParams {
  type: "DayOffice";
  spaceID?: string;
  quantity?: number;
  dateRange?: DayPassDateRange;
  promoCode?: string;
  physicalSpaceID?: string;
  email?: string;
  attendees?: number;
  addOns?: AddOnOrderParams[];
}
export interface MembershipOrderParams {
  type: "Membership";
  promoCode?: string;
  email?: string;
  city?: string;
  spaceID?: string;
  locationID?: string;
  membershipID?: string;
  subscriptionInterval?: string;
  subscriptionStartDate?: string;
}

// TODO: Replace with MeetingRoomOrderParams
export interface HourlyPassOrderParams {
  type: "Hourly";
  spaceID?: string;
  dateRange?: HourlyPassDateRange;
  promoCode?: string;
  email?: string;
  attendees?: number;
  addOns?: AddOnOrderParams[];
}

interface MultiOrderParams {
  spaceID?: string;
  addOnID?: string;
  spaceOfferingID?: string;
  promoCode?: string;
  email?: string;
  attendees?: number;
}

export interface MultiPassOrderParams extends MultiOrderParams {
  type: TemplateName.MultiPass;
  quantity?: number;
}

export interface MultiDayOfficeOrderParams extends MultiOrderParams {
  type: TemplateName.MultiDayOffice;
  partnerOfferingLocationID?: string;
  capacity?: number;
}

export interface DayPassDateRange {
  startDate?: Day;
  endDate?: Day;
}

export interface MeetingRoomDateRange {
  date?: Day;
  startTime?: Time;
  endTime?: Time;
}

interface HourlyPassDateRange {
  date?: Day;
  startTime?: Time;
  endTime?: Time;
}

export type OrderParams =
  | MeetingRoomOrderParams
  | EventSpaceOrderParams
  | DayPassWithCapacityOrderParams
  | DayPassOrderParams
  | DayOfficeOrderParams
  | DailyAccessOrderParams
  | MultiPassOrderParams
  | MultiDayOfficeOrderParams
  | HourlyPassOrderParams
  | DailyOfficeOrderParams
  | MembershipOrderParams;

// Day strings arrive from the URL, where a truncated or hand-edited one parses
// to an invalid date. Normalizing drops the caller's hint rather than carrying a
// value the pickers, formatters and quote input downstream cannot render.
function hasUnusableDay(...days: (string | undefined)[]): boolean {
  return days.some((day) => !!day && !isValidDay(day));
}

export function normalizeDayPassOrderParams(
  queryParams: OfferingPageQueryParams,
  includeQuantity?: boolean
): DayPassOrderParams {
  if (queryParams.startDate && !isValidDay(queryParams.startDate)) {
    return {
      type: TemplateName.DayPass,
      spaceID: queryParams.spaceID,
      promoCode: queryParams.promoCode,
      quantity: includeQuantity ? Number(queryParams.quantity) || 1 : undefined,
    };
  }
  if (queryParams.endDate && !isValidDay(queryParams.endDate)) {
    return {
      type: TemplateName.DayPass,
      spaceID: queryParams.spaceID,
      promoCode: queryParams.promoCode,
      quantity: includeQuantity ? Number(queryParams.quantity) || 1 : undefined,
    };
  }

  return {
    type: TemplateName.DayPass,
    spaceID: queryParams.spaceID,
    promoCode: queryParams.promoCode,
    quantity: includeQuantity ? Number(queryParams.quantity) || 1 : undefined,
    dateRange: {
      startDate: queryParams.startDate,
      endDate: queryParams.endDate,
    },
  };
}

export function normalizeDayPassWithCapacityOrderParams(
  queryParams: OfferingPageQueryParams
): DayPassWithCapacityOrderParams {
  if (queryParams.startDate && !isValidDay(queryParams.startDate)) {
    return {
      type: "DayPassWithCapacity",
      spaceID: queryParams.spaceID,
      partnerOfferingLocationID: queryParams.partnerOfferingLocationID,
      promoCode: queryParams.promoCode,
      capacity: queryParams.capacity
        ? Number(queryParams.capacity) || 1
        : undefined,
    };
  }
  if (queryParams.endDate && !isValidDay(queryParams.endDate)) {
    return {
      type: "DayPassWithCapacity",
      spaceID: queryParams.spaceID,
      partnerOfferingLocationID: queryParams.partnerOfferingLocationID,
      promoCode: queryParams.promoCode,
      capacity: queryParams.capacity
        ? Number(queryParams.capacity) || 1
        : undefined,
    };
  }

  return {
    type: "DayPassWithCapacity",
    spaceID: queryParams.spaceID,
    partnerOfferingLocationID: queryParams.partnerOfferingLocationID,
    promoCode: queryParams.promoCode,
    capacity: queryParams.capacity
      ? Number(queryParams.capacity) || 1
      : undefined,
    dateRange: {
      startDate: queryParams.startDate,
      endDate: queryParams.endDate,
    },
  };
}

export function normalizeDailyOfficeOrderParams(
  queryParams: OfferingPageQueryParams
): DailyOfficeOrderParams {
  if (queryParams.startDate && !isValidDay(queryParams.startDate)) {
    return {
      type: "DailyOffice",
      spaceID: queryParams.spaceID,
      partnerOfferingLocationID: queryParams.partnerOfferingLocationID,
      addOnID: queryParams.addOnID || "",
      spaceOfferingID: queryParams.spaceOfferingID || "",
      promoCode: queryParams.promoCode,
      email: queryParams.email,
      capacity: queryParams.capacity
        ? Number(queryParams.capacity) || 1
        : undefined,
      city: queryParams.city,
    };
  }
  if (queryParams.endDate && !isValidDay(queryParams.endDate)) {
    return {
      type: "DailyOffice",
      spaceID: queryParams.spaceID,
      partnerOfferingLocationID: queryParams.partnerOfferingLocationID,
      addOnID: queryParams.addOnID || "",
      spaceOfferingID: queryParams.spaceOfferingID || "",
      promoCode: queryParams.promoCode,
      email: queryParams.email,
      capacity: queryParams.capacity
        ? Number(queryParams.capacity) || 1
        : undefined,
      city: queryParams.city,
    };
  }

  return {
    type: "DailyOffice",
    spaceID: queryParams.spaceID,
    partnerOfferingLocationID: queryParams.partnerOfferingLocationID,
    promoCode: queryParams.promoCode,
    addOnID: queryParams.addOnID || "",
    spaceOfferingID: queryParams.spaceOfferingID || "",
    city: queryParams.city,
    email: queryParams.email,
    capacity: queryParams.capacity
      ? Number(queryParams.capacity) || 1
      : undefined,
    dateRange: parseDailyOfficeDateRangeQuery(queryParams),
    addOns: queryParams.addOns
      ? parseAddOnQueryParams(queryParams.addOns)
      : undefined,
  };
}

// Hourly day-office bookings carry { date, startTime, endTime } query params
// (mirroring meeting rooms); daily ones carry { startDate, endDate }. Honor
// hourly params first when both happen to be present so a shareable hourly
// link round-trips correctly.
function parseDailyOfficeDateRangeQuery(
  queryParams: OfferingPageQueryParams
): DateTimeValue | undefined {
  if (queryParams.addOnID !== "dayoffice") {
    return undefined;
  }
  if (queryParams.date) {
    return {
      date: queryParams.date,
      startTime: queryParams.startTime,
      endTime: queryParams.endTime,
    };
  }
  return {
    startDate: queryParams.startDate,
    endDate: queryParams.endDate,
  };
}

function parseAddOnQueryParams(param: string): AddOnOrderParams[] | undefined {
  try {
    const addOns: AddOnsQueryParams[] = JSON.parse(param);
    const addOnList = [];
    for (const addOn of addOns) {
      for (const item of addOn.value) {
        addOnList.push({
          spaceOfferingID: addOn.spaceOfferingID,
          quantity: item.quantity,
          date: item.date,
        });
      }
    }
    return addOnList;
  } catch (error) {
    console.error("Error parsing addOns query param\n", error);
  }
}

export function normalizeDailyAccessOrderParams(
  queryParams: OfferingPageQueryParams,
  includeQuantity?: boolean
): DailyAccessOrderParams {
  if (queryParams.startDate && !isValidDay(queryParams.startDate)) {
    return {
      type: "DailyAccess",
      spaceID: queryParams.spaceID,
      promoCode: queryParams.promoCode,
      email: queryParams.email,
      addOnID: queryParams.addOnID || "",
      spaceOfferingID: queryParams.spaceOfferingID || "",
      quantity: includeQuantity ? Number(queryParams.quantity) || 1 : undefined,
      city: queryParams.city,
    };
  }
  if (queryParams.endDate && !isValidDay(queryParams.endDate)) {
    return {
      type: "DailyAccess",
      spaceID: queryParams.spaceID,
      promoCode: queryParams.promoCode,
      email: queryParams.email,
      addOnID: queryParams.addOnID || "",
      spaceOfferingID: queryParams.spaceOfferingID || "",
      quantity: includeQuantity ? Number(queryParams.quantity) || 1 : undefined,
      city: queryParams.city,
    };
  }

  return {
    type: "DailyAccess",
    spaceID: queryParams.spaceID,
    promoCode: queryParams.promoCode,
    email: queryParams.email,
    addOnID: queryParams.addOnID || "",
    spaceOfferingID: queryParams.spaceOfferingID || "",
    quantity: includeQuantity ? Number(queryParams.quantity) || 1 : undefined,
    city: queryParams.city,
    dateRange: parseDayPassDateRangeQuery(queryParams),
    addOns: queryParams.addOns
      ? parseAddOnQueryParams(queryParams.addOns)
      : undefined,
  };
}

// Day passes carry either an hourly window ({ date, startTime, endTime }) or a
// daily range ({ startDate, endDate }) in the URL. Mirrors
// parseDailyOfficeDateRangeQuery so the hourly selection survives the
// details -> checkout navigation (the checkout step re-seeds from the query string).
function parseDayPassDateRangeQuery(
  queryParams: OfferingPageQueryParams
): DateTimeValue | undefined {
  if (queryParams.addOnID !== "daypass") {
    return undefined;
  }
  if (queryParams.date) {
    return {
      date: queryParams.date,
      startTime: queryParams.startTime,
      endTime: queryParams.endTime,
    };
  }
  return {
    startDate: queryParams.startDate,
    endDate: queryParams.endDate,
  };
}

export function normalizeMembershipOrderParams(
  queryParams: OfferingPageQueryParams,
  membershipV2FetureFlag: boolean
): MembershipOrderParams {
  return {
    type: "Membership",
    promoCode: queryParams.promoCode,
    email: queryParams.email,
    city: queryParams.city,
    locationID: queryParams.locationID,
    membershipID: queryParams.membershipID,
    subscriptionInterval: queryParams.subscriptionInterval,
    subscriptionStartDate: membershipV2FetureFlag
      ? queryParams.subscriptionStartDate
      : undefined,
  };
}

export function normalizeDayOfficeOrderParams(
  queryParams: OfferingPageQueryParams,
  includeQuantity?: boolean
): DayOfficeOrderParams {
  if (queryParams.startDate && !isValidDay(queryParams.startDate)) {
    return {
      type: "DayOffice",
      spaceID: queryParams.spaceID,
      promoCode: queryParams.promoCode,
      physicalSpaceID: queryParams.physicalSpaceID,
      quantity: includeQuantity ? Number(queryParams.quantity) || 1 : undefined,
    };
  }
  if (queryParams.endDate && !isValidDay(queryParams.endDate)) {
    return {
      type: "DayOffice",
      spaceID: queryParams.spaceID,
      promoCode: queryParams.promoCode,
      physicalSpaceID: queryParams.physicalSpaceID,
      quantity: includeQuantity ? Number(queryParams.quantity) || 1 : undefined,
    };
  }

  return {
    type: "DayOffice",
    spaceID: queryParams.spaceID,
    promoCode: queryParams.promoCode,
    physicalSpaceID: queryParams.physicalSpaceID,
    quantity: includeQuantity ? Number(queryParams.quantity) || 1 : undefined,
    dateRange: {
      startDate: queryParams.startDate,
      endDate: queryParams.endDate,
    },
  };
}

export function normalizeMeetingRoomOrderParams(
  queryParams: OfferingPageQueryParams
): MeetingRoomOrderParams {
  // `date` is checked alongside the range: an hourly booking carries its day
  // there, and surfaces seed the picker's visible months from whichever of the
  // three the URL used, so letting an unusable one through poisons the same
  // consumers either way.
  if (
    hasUnusableDay(queryParams.startDate, queryParams.endDate, queryParams.date)
  ) {
    return {
      type: "MeetingRoom",
      spaceID: queryParams.spaceID,
      promoCode: queryParams.promoCode,
      email: queryParams.email,
    };
  }

  // A rule and add-ons never travel together: add-ons are priced per booking
  // date, so a URL carrying both would quote and charge them for one date
  // against many with no control on the page to remove them.
  const recurrence = parseRepeatParam(queryParams.repeat);
  const addOns =
    recurrence || !queryParams.addOns
      ? undefined
      : parseAddOnQueryParams(queryParams.addOns);

  if (queryParams.startDate) {
    return {
      type: "MeetingRoom",
      spaceID: queryParams.spaceID,
      promoCode: queryParams.promoCode,
      email: queryParams.email,
      layoutID: queryParams.layoutID,
      attendees: queryParams.attendees
        ? parseInt(queryParams.attendees)
        : undefined,
      dateRange: {
        startDate: queryParams.startDate,
        endDate: queryParams.endDate,
      },
      addOns,
      recurrence,
    };
  }

  return {
    type: "MeetingRoom",
    spaceID: queryParams.spaceID,
    promoCode: queryParams.promoCode,
    email: queryParams.email,
    layoutID: queryParams.layoutID,
    attendees: queryParams.attendees
      ? parseInt(queryParams.attendees)
      : undefined,
    dateRange: {
      date: queryParams.date,
      startTime: queryParams.startTime,
      endTime: queryParams.endTime,
    },
    addOns,
    recurrence,
  };
}

// Add-ons are priced per booking date, so the surfaces that sell them ask
// this before offering any with a recurring booking.
export function isRecurringOrderParams(orderParams: OrderParams): boolean {
  return "recurrence" in orderParams && !!orderParams.recurrence;
}

function toEventSpaceDateRange(
  queryParams: OfferingPageQueryParams
): DateTimeValue | undefined {
  if (
    hasUnusableDay(queryParams.startDate, queryParams.endDate, queryParams.date)
  ) {
    return undefined;
  }
  if (queryParams.startDate && queryParams.endDate) {
    return { startDate: queryParams.startDate, endDate: queryParams.endDate };
  }
  return {
    date: queryParams.date,
    startTime: queryParams.startTime,
    endTime: queryParams.endTime,
  };
}

export function normalizeEventSpaceOrderParams(
  queryParams: OfferingPageQueryParams
): EventSpaceOrderParams {
  return {
    type: "EventSpace",
    layoutID: queryParams.layoutID,
    email: queryParams.email,
    eventTypeID: queryParams.eventTypeID,
    eventTypeDescription: queryParams.eventTypeDescription,
    attendees: queryParams.attendees
      ? parseInt(queryParams.attendees)
      : undefined,
    dateRange: toEventSpaceDateRange(queryParams),
    promoCode: queryParams.promoCode,
    addOns: queryParams.addOns
      ? parseAddOnQueryParams(queryParams.addOns)
      : undefined,
  };
}

export function normalizeMultiPassOrderParams(
  queryParams: OfferingPageQueryParams
): MultiPassOrderParams {
  return {
    type: TemplateName.MultiPass,
    spaceID: queryParams.spaceID,
    addOnID: queryParams.addOnID,
    spaceOfferingID: queryParams.spaceOfferingID,
    promoCode: queryParams.promoCode,
  };
}

export function normalizeMultiDayOfficeOrderParams(
  queryParams: OfferingPageQueryParams
): MultiDayOfficeOrderParams {
  return {
    type: TemplateName.MultiDayOffice,
    addOnID: queryParams.addOnID,
    promoCode: queryParams.promoCode,
    spaceOfferingID: queryParams.spaceOfferingID,
    spaceID: queryParams.spaceID,
    partnerOfferingLocationID: queryParams.partnerOfferingLocationID,
    capacity: queryParams.capacity ? Number(queryParams.capacity) : undefined,
  };
}

//TODO refactor this name: legacy details to some meaningful name like spaceLayout
export function toQuoteInput(
  spaceID: string,
  partnerID: string,
  orderParams: OrderParams,
  source: OrderSource,
  legacy__details: { attendees: number; layoutID: string },
  membershipDetails?: OrderItemMembershipInput["details"],
  availableDays?: AvailableDay[],
  partnerOfferingID?: string,
  // The expanded repeat rule of a recurring meeting-room booking. Attached only
  // when it holds enough dates to be one; below that the quote is the single
  // booking the anchor describes.
  recurrence?: RecurrenceInput
): QuoteInput | null {
  const orderItems: OrderItemInput[] = [];
  if (orderParams.type === "DayOffice") {
    if (!orderParams.dateRange?.startDate || !orderParams.dateRange?.endDate) {
      return null;
    }

    const bookingDates = availableDays
      ? getNonConsecutiveDates(
          orderParams.dateRange.startDate,
          orderParams.dateRange.endDate,
          availableDays
        )
      : undefined;

    orderItems.push({
      type: OrderableType.Booking,
      quantity: orderParams.quantity,
      details: {
        spaceID,
        type: BookingType.Daily,
        startDate: orderParams.dateRange.startDate,
        endDate: orderParams.dateRange.endDate,
        physicalSpaceID: orderParams.physicalSpaceID,
        bookingDates,
        legacy__details: legacy__details,
      },
      spaceOfferings: orderParams.addOns,
    });
  } else if (orderParams.type === TemplateName.DayPass) {
    if (!orderParams.dateRange?.startDate || !orderParams.dateRange?.endDate) {
      return null;
    }

    const bookingDates = availableDays
      ? getNonConsecutiveDates(
          orderParams.dateRange.startDate,
          orderParams.dateRange.endDate,
          availableDays
        )
      : undefined;

    orderItems.push({
      type: OrderableType.Booking,
      quantity: orderParams.quantity,
      details: {
        spaceID,
        type: BookingType.Daily,
        startDate: orderParams.dateRange.startDate,
        endDate: orderParams.dateRange.endDate,
        bookingDates,
        legacy__details: legacy__details,
      },
      spaceOfferings: orderParams.addOns,
    });
  } else if (orderParams.type === "DayPassWithCapacity") {
    if (!orderParams.dateRange?.startDate || !orderParams.dateRange?.endDate) {
      return null;
    }

    orderItems.push({
      type: OrderableType.Booking,
      details: {
        spaceID,
        type: BookingType.Daily,
        startDate: orderParams.dateRange.startDate,
        endDate: orderParams.dateRange.endDate,
        legacy__details: legacy__details,
      },
    });
  } else if (orderParams.type === TemplateName.MultiPass) {
    if (!orderParams.addOnID || !orderParams.spaceOfferingID) {
      return null;
    }

    orderItems.push({
      type: OrderableType.AddOn,
      details: {
        type: AddOnType.MultiPass,
        spaceID,
        addOnID: orderParams.addOnID,
        spaceOfferingID: orderParams.spaceOfferingID,
      },
    });
  } else if (orderParams.type === TemplateName.MultiDayOffice) {
    if (!orderParams.addOnID || !orderParams.spaceOfferingID) {
      return null;
    }

    orderItems.push({
      type: OrderableType.AddOn,
      details: {
        type: AddOnType.MultiPass,
        spaceID,
        addOnID: orderParams.addOnID,
        spaceOfferingID: orderParams.spaceOfferingID,
        capacity: orderParams.capacity,
      },
    });
  } else if (
    orderParams.type === "Hourly" ||
    orderParams.type === "MeetingRoom"
  ) {
    if (isHourlyValue(orderParams.dateRange)) {
      if (
        !orderParams.dateRange?.date ||
        !orderParams.dateRange.startTime ||
        !orderParams.dateRange.endTime
      ) {
        return null;
      }
      orderItems.push({
        type: OrderableType.Booking,
        quantity: undefined,
        details: {
          type: BookingType.Hourly,
          spaceID,
          date: orderParams.dateRange.date,
          startTime: orderParams.dateRange.startTime,
          endTime: orderParams.dateRange.endTime,
          recurrence: recurringInputFor(orderParams, recurrence),
          legacy__details: legacy__details,
        },
        spaceOfferings: orderParams.addOns,
      });
    } else {
      if (orderParams.dateRange?.startDate && orderParams.dateRange?.endDate) {
        const bookingDates = availableDays
          ? getNonConsecutiveDates(
              orderParams.dateRange.startDate,
              orderParams.dateRange.endDate,
              availableDays
            )
          : undefined;

        orderItems.push({
          type: OrderableType.Booking,
          quantity: undefined,
          details: {
            type: BookingType.Daily,
            spaceID,
            startDate: orderParams.dateRange.startDate,
            endDate: orderParams.dateRange.endDate,
            bookingDates: bookingDates,
            recurrence: recurringInputFor(orderParams, recurrence),
            legacy__details: legacy__details,
          },
          spaceOfferings: orderParams.addOns,
        });
      } else {
        return null;
      }
    }
  } else if (orderParams.type === "EventSpace") {
    if (isHourlyValue(orderParams.dateRange)) {
      if (orderParams.dateRange.startTime && orderParams.dateRange.endTime) {
        orderItems.push({
          type: OrderableType.Booking,
          quantity: undefined,
          details: {
            type: BookingType.Hourly,
            spaceID,
            date: orderParams.dateRange.date,
            startTime: orderParams.dateRange.startTime,
            endTime: orderParams.dateRange.endTime,
            eventTypeId: orderParams.eventTypeID,
            eventTypeDescription: orderParams.eventTypeDescription,
            legacy__details: legacy__details,
          },
          spaceOfferings: orderParams.addOns,
        });
      } else {
        return null;
      }
    } else {
      if (orderParams.dateRange?.startDate && orderParams.dateRange?.endDate) {
        const bookingDates = availableDays
          ? getNonConsecutiveDates(
              orderParams.dateRange.startDate,
              orderParams.dateRange.endDate,
              availableDays
            )
          : undefined;
        orderItems.push({
          type: OrderableType.Booking,
          quantity: undefined,
          details: {
            type: BookingType.Daily,
            spaceID,
            startDate: orderParams.dateRange.startDate,
            endDate: orderParams.dateRange.endDate,
            eventTypeId: orderParams.eventTypeID,
            eventTypeDescription: orderParams.eventTypeDescription,
            bookingDates,
            legacy__details: legacy__details,
          },
          spaceOfferings: orderParams.addOns,
        });
      } else {
        return null;
      }
    }
  } else if (orderParams.type === "DailyAccess") {
    // The "daypass" sentinel books the space itself and never reads the
    // catalog identifiers, so it needs no spaceOfferingID — FM amendment
    // quotes build this shape without any offering context. Multi-pass
    // packages still require both IDs (their order item carries them).
    if (orderParams.addOnID === "daypass") {
      if (isHourlyValue(orderParams.dateRange)) {
        if (
          !orderParams.dateRange.date ||
          !orderParams.dateRange.startTime ||
          !orderParams.dateRange.endTime
        ) {
          return null;
        }
        // Day-pass hourly keeps quantity (a team books Q passes for one window) —
        // unlike day offices, which send quantity: undefined.
        orderItems.push({
          type: OrderableType.Booking,
          quantity: orderParams.quantity,
          details: {
            spaceID,
            type: BookingType.Hourly,
            date: orderParams.dateRange.date,
            startTime: orderParams.dateRange.startTime,
            endTime: orderParams.dateRange.endTime,
            legacy__details: legacy__details,
          },
          spaceOfferings: orderParams.addOns,
        });
      } else if (
        orderParams.dateRange?.startDate &&
        orderParams.dateRange?.endDate
      ) {
        orderItems.push({
          type: OrderableType.Booking,
          quantity: orderParams.quantity,
          details: {
            spaceID,
            type: BookingType.Daily,
            startDate: orderParams.dateRange.startDate,
            endDate: orderParams.dateRange.endDate,
            legacy__details: legacy__details,
          },
          spaceOfferings: orderParams.addOns,
        });
      } else {
        return null;
      }
    } else {
      if (!orderParams.addOnID || !orderParams.spaceOfferingID) {
        return null;
      }
      orderItems.push({
        type: OrderableType.AddOn,
        details: {
          type: AddOnType.MultiPass,
          spaceID,
          addOnID: orderParams.addOnID,
          spaceOfferingID: orderParams.spaceOfferingID,
        },
      });
    }
  } else if (orderParams.type === "DailyOffice") {
    // The "dayoffice" sentinel books the space itself and never reads the
    // catalog identifiers, so it needs no spaceOfferingID — FM amendment
    // quotes build this shape without any offering context. Multi-pass
    // packages still require both IDs (their order item carries them).
    if (orderParams.addOnID === "dayoffice") {
      if (isHourlyValue(orderParams.dateRange)) {
        if (
          !orderParams.dateRange.date ||
          !orderParams.dateRange.startTime ||
          !orderParams.dateRange.endTime
        ) {
          return null;
        }
        orderItems.push({
          type: OrderableType.Booking,
          quantity: undefined,
          details: {
            spaceID,
            type: BookingType.Hourly,
            date: orderParams.dateRange.date,
            startTime: orderParams.dateRange.startTime,
            endTime: orderParams.dateRange.endTime,
            legacy__details: legacy__details,
          },
          spaceOfferings: orderParams.addOns,
        });
      } else if (
        orderParams.dateRange?.startDate &&
        orderParams.dateRange?.endDate
      ) {
        orderItems.push({
          type: OrderableType.Booking,
          details: {
            spaceID,
            type: BookingType.Daily,
            startDate: orderParams.dateRange.startDate,
            endDate: orderParams.dateRange.endDate,
            legacy__details: legacy__details,
          },
          spaceOfferings: orderParams.addOns,
        });
      } else {
        return null;
      }
    } else {
      if (!orderParams.addOnID || !orderParams.spaceOfferingID) {
        return null;
      }
      orderItems.push({
        type: OrderableType.AddOn,
        details: {
          type: AddOnType.MultiPass,
          spaceID,
          addOnID: orderParams.addOnID,
          spaceOfferingID: orderParams.spaceOfferingID,
        },
      });
    }
  } else if (orderParams.type === "Membership" && membershipDetails) {
    orderItems.push({
      details: {
        membershipID: membershipDetails.membershipID,
        subscriptionInterval: membershipDetails.subscriptionInterval,
        subscriptionStartDate: membershipDetails.subscriptionStartDate,
      },
    });
  }

  return {
    source,
    email: orderParams.email || "",
    partnerID,
    promoCode: orderParams.promoCode || "",
    orderItems,
    partnerOfferingID,
  };
}

// A recurring booking is meeting-room only and needs at least two dates; a
// daily one is one day per date, so a span anchor cannot repeat. Anything else
// quotes as the single booking the anchor describes.
function recurringInputFor(
  orderParams: OrderParams,
  recurrence?: RecurrenceInput
): RecurrenceInput | undefined {
  if (!recurrence || orderParams.type !== "MeetingRoom") return undefined;
  if (recurrence.dates.length < MIN_RECURRING_COUNT) return undefined;
  const { dateRange } = orderParams;
  if (
    dateRange &&
    !isHourlyValue(dateRange) &&
    dateRange.startDate !== dateRange.endDate
  ) {
    return undefined;
  }
  return recurrence;
}

export function useDateTimePickerHelper(
  spaceID: string | undefined,
  options?: {
    dateRange?: DayPassDateRange | undefined;
    // Opt out of the per-date timeslot fetch when the consuming surface has no
    // hourly UI (e.g. a daily-only day office). Default true preserves the
    // meeting-room behavior. When false, `timeslots` returns [] and no network
    // request fires after `handleSelectDate` sets the internal `date` state.
    enableTimeSlots?: boolean;
    // The selected meeting-room layout, so per-slot prices reflect that layout's
    // rate on a per-layout-priced space (matches the calendar + better_hour_rates).
    spaceLayoutId?: string;
  }
) {
  const enableTimeSlots = options?.enableTimeSlots ?? true;
  const [date, setDate] = useState(options?.dateRange?.startDate || "");
  const handleSelectDate = useCallback((date: Date) => {
    setDate(toDay(date));
  }, []);

  const [dates, setDates] = useState(() =>
    toMonthWindow(options?.dateRange?.startDate, options?.dateRange?.endDate)
  );

  const {
    data: { availableDays, config },
    loading: availableDaysLoading,
    error: availableDaysError,
  } = useGetSpaceAvailableDays(spaceID, {
    startDate: toDay(dates.start),
    endDate: toDay(dates.end),
  });

  const {
    data: availableTimeSlots,
    loading: availableTimeSlotsLoading,
    error: availableTimeSlotsError,
  } = useGetSpaceAvailableTimeSlots(
    spaceID,
    enableTimeSlots ? date : undefined,
    undefined,
    options?.spaceLayoutId
  );

  let showSegmentedControl = true;
  let initialBookingType = BookingType.Hourly;

  const isOutsideRange = useCallback(
    (date: Date) => {
      return (
        isBefore(date, startOfDay(new Date())) ||
        isAfter(
          date,
          addDays(dates.start, NUMBER_OF_DAYS_CHECK_FOR_AVAILABILITY)
        )
      );
    },
    [dates.start]
  );

  const isBlockedDate = useCallback(
    (date: Date, requireFullDay = false) => {
      // While availability is still loading, availableDays is undefined; don't
      // treat every day as blocked, or the picker's loading state never shows
      // (the blocked/strikethrough branch would win over the loading branch).
      if (!availableDays) {
        return false;
      }
      return isDateBlockedByAvailableDays({
        availableDays,
        date,
        requireFullDay,
      });
    },
    [availableDays]
  );

  const handleNextVisibleDates = useCallback(
    (nextStartDate: Date, nextEndDate: Date) => {
      setDates({
        start: startOfMonth(nextStartDate),
        end: endOfMonth(nextEndDate),
      });
    },
    []
  );

  return {
    initialBookingType,
    showSegmentedControl,
    isOutsideRange,
    isBlockedDate,
    timeslots: availableTimeSlots,
    availableDaysLoading,
    availableDaysError,
    availableTimeSlotsLoading,
    availableTimeSlotsError,
    handleNextVisibleDates,
    handleSelectDate,
    availableDays,
    disableRangeSelection: availableDaysLoading
      ? undefined
      : !config.multiDayBookingAllowed,
  };
}

export function useSpaceAvailableDaysQuery(
  spaceID?: string,
  options?: {
    capacity?: number;
    physicalSpaceID?: string;
    dateRange?: DateTimeValue;
    subscriptionId?: string;
    subscriptionStartDate?: string;
    quantity?: number;
  }
) {
  const capacity = options?.capacity;
  const physicalSpaceID = options?.physicalSpaceID;
  const dateRange = options?.dateRange;
  const subscriptionId = options?.subscriptionId;
  const subscriptionStartDate = options?.subscriptionStartDate;
  const quantity = options?.quantity;
  // Only a daily range seeds the visible month; an hourly day pass has no
  // start/end date, so it falls back to the default window (the picker shows all days).
  const dailyDateRange = isHourlyValue(dateRange) ? undefined : dateRange;
  const [dates, setDates] = useState(() =>
    toMonthWindow(dailyDateRange?.startDate, dailyDateRange?.endDate)
  );

  const {
    data: { availableDays, config },
    loading: availableDaysLoading,
  } = useGetSpaceAvailableDays(spaceID, {
    startDate: toDay(dates.start),
    endDate: toDay(dates.end),
    capacity,
    physicalSpaceID,
    subscriptionId,
    quantity,
  });

  const isOutsideRange = useCallback(
    (date: Date) => {
      let startDate = startOfDay(new Date());
      if (
        subscriptionStartDate &&
        isBefore(startDate, parseISO(subscriptionStartDate))
      ) {
        startDate = parseISO(subscriptionStartDate);
      }
      return (
        isBefore(date, startDate) ||
        isAfter(
          date,
          addDays(dates.start, NUMBER_OF_DAYS_CHECK_FOR_AVAILABILITY)
        )
      );
    },
    [dates.start, subscriptionStartDate]
  );

  const isBlockedDate = useCallback(
    (date: Date) => {
      if (!spaceID || !availableDays) {
        return false;
      }

      return isDateBlockedByAvailableDays({
        availableDays,
        date,
        requireFullDay: true,
      });
    },
    [spaceID, availableDays]
  );

  const handleNextVisibleDates = useCallback(
    (nextStartDate: Date, nextEndDate: Date) => {
      setDates({
        start: startOfMonth(nextStartDate),
        end: endOfMonth(nextEndDate),
      });
    },
    []
  );

  return {
    isOutsideRange,
    isBlockedDate,
    handleNextVisibleDates,
    availableDays,
    availableDaysLoading,
    config,
  };
}

export function useMultiPassOrderHelpers(
  partnerID: string,
  multiPassID?: string,
  allowPastDates?: boolean
) {
  const {
    data: availability,
    loading,
    error,
  } = useMultiPassAvailabilityQuery(partnerID, multiPassID);

  const isOutsideRange = useCallback(
    (date: Date) => {
      const isFutureDate = isAfter(date, endOfMonth(addMonths(new Date(), 6)));
      if (allowPastDates) {
        return isFutureDate;
      } else {
        return isBefore(date, startOfDay(new Date())) || isFutureDate;
      }
    },
    [allowPastDates]
  );

  const isBlockedDate = useCallback(
    (date: Date) => {
      if (!multiPassID || !availability) {
        return false;
      }

      const dateAvailability = availability.availableDays.find(
        (d) => d.date === toDay(date)
      );
      if (!dateAvailability) {
        if (allowPastDates) {
          const isPastDate = isBefore(date, startOfDay(new Date()));
          return !isPastDate;
        }
        return true;
      }

      if (dateAvailability.availableFullDay === false) {
        return true;
      }

      return false;
    },
    [multiPassID, availability, allowPastDates]
  );

  return {
    isOutsideRange,
    isBlockedDate,
    availableDays: availability?.availableDays,
    loading,
    error,
  };
}

type OrderParamError =
  | DayOfficeOrderParamsErrors
  | DailyAccessOrderParamsErrors
  | DayPassOrderParamsErrors
  | DayPassWithCapacityOrderParamsErrors
  | MeetingRoomOrderParamsErrors
  | MultiPassOrderParamsErrors
  | MultiDayOfficeOrderParamsErrors;

function useHandleChangeEmail(
  setOrderParams: Function,
  setOrderParamsError: Function
) {
  return useCallback(
    (email?: string) => {
      setOrderParams((prevOrderParams: OrderParams) => ({
        ...prevOrderParams,
        email,
      }));
      setOrderParamsError((prev: OrderParamError) => ({
        ...prev,
        email: "",
      }));
    },
    [setOrderParams, setOrderParamsError]
  );
}

export interface DayOfficeOrderParamsErrors {
  dateRange: string;
  spaceID: string;
  promoCode: string;
  quantity: string;
  email: string;
}

export function useDayOfficeOrderParamsHandlers(
  initialDayOfficeOrderParams?: DayOfficeOrderParams
) {
  const [orderParams, setOrderParams] = useState(
    initialDayOfficeOrderParams || normalizeDayOfficeOrderParams({})
  );
  const { fbt } = useI18n();
  const [orderParamsError, setOrderParamsError] =
    useState<DayOfficeOrderParamsErrors>({
      dateRange: "",
      spaceID: "",
      promoCode: "",
      quantity: "",
      email: "",
    });
  const handleChangeLocation = useCallback((spaceID: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      spaceID,
      dateRange: undefined,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      spaceID: "",
      dateRange: "",
      promoCode: "",
      quantity: "",
      email: "",
    }));
  }, []);

  const handleChangeQuantity = useCallback((quantity: number) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      quantity,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      spaceID: "",
      dateRange: "",
      promoCode: "",
      quantity: "",
      email: "",
    }));
  }, []);

  const handleChangeDayOfficeDateRange = useCallback(
    (dateRange?: DayPassDateRange) => {
      setOrderParams((prevOrderParams) => ({
        ...prevOrderParams,
        dateRange,
      }));
      setOrderParamsError((prev) => ({
        ...prev,
        dateRange: "",
      }));
    },
    []
  );

  const validateOrderParams = useCallback(() => {
    if (!orderParams.spaceID) {
      setOrderParamsError((prev) => ({
        ...prev,
        spaceID: fbt("Please select a location"),
      }));

      return false;
    }

    if (!orderParams.dateRange?.startDate || !orderParams.dateRange.endDate) {
      setOrderParamsError((prev) => ({
        ...prev,
        dateRange: fbt("Please select date range"),
      }));
      return false;
    }

    return true;
  }, [orderParams, fbt]);

  const handleChangePromoCode = useCallback((promoCode: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      promoCode,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      promoCode: "",
    }));
  }, []);

  const handleChangeEmail = useHandleChangeEmail(
    setOrderParams,
    setOrderParamsError
  );

  let errorText = "";
  if (orderParamsError.spaceID) {
    errorText = orderParamsError.spaceID;
  } else if (orderParamsError.dateRange) {
    errorText = orderParamsError.dateRange;
  } else if (orderParamsError.quantity) {
    errorText = orderParamsError.quantity;
  }

  return {
    validateOrderParams,
    handleChangeQuantity,
    handleChangeDayOfficeDateRange,
    handleChangeLocation,
    handleChangePromoCode,
    handleChangeEmail,
    orderParams,
    orderParamsError,
    errorText,
  };
}

export interface DailyAccessOrderParamsErrors {
  dateRange: string;
  spaceID: string;
  addOnID: string;
  spaceOfferingID: string;
  promoCode: string;
  quantity: string;
  email: string;
}

export interface MembershipOrderParamsErrors {
  promoCode: string;
  email: string;
  locationID: string;
  subscriptionStartDate: string;
}

export function useDailyAccessOrderParamsHandlers(
  whitelistedAddOns: SpaceOffering[],
  initialDailyAccessOrderParams?: DailyAccessOrderParams
) {
  const { promoCode } = useQueryString<{ promoCode?: string }>();

  const [orderParams, setOrderParams] = useState(
    initialDailyAccessOrderParams || normalizeDailyAccessOrderParams({})
  );
  const { fbt } = useI18n();
  const [orderParamsError, setOrderParamsError] =
    useState<DailyAccessOrderParamsErrors>({
      dateRange: "",
      spaceID: "",
      promoCode: "",
      addOnID: "",
      spaceOfferingID: "",
      quantity: "",
      email: "",
    });

  const handleChangeCity = useCallback((city: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      city,
      spaceID: undefined,
      dateRange: undefined,
      addOnID: undefined,
      spaceOfferingID: undefined,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      spaceID: "",
      dateRange: "",
      promoCode: "",
      addOnID: "",
      spaceOfferingID: "",
      quantity: "",
      email: "",
    }));
  }, []);

  const handleChangeLocation = useCallback((spaceID: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      spaceID,
      dateRange: undefined,
      addOnID: undefined,
      spaceOfferingID: undefined,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      spaceID: "",
      dateRange: "",
      promoCode: "",
      addOnID: "",
      spaceOfferingID: "",
      quantity: "",
      email: "",
    }));
  }, []);

  const handleChangeQuantity = useCallback((quantity: number) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      quantity,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      spaceID: "",
      dateRange: "",
      promoCode: "",
      quantity: "",
      email: "",
    }));
  }, []);

  const handleChangeAddOnID = useCallback(
    (addOnID: string, spaceOfferingID: string) => {
      setOrderParams((prevOrderParams) => {
        if (addOnID) {
          return {
            ...prevOrderParams,
            addOnID,
            spaceOfferingID,
            dateRange: undefined,
            quantity: 1,
          };
        }

        return {
          ...prevOrderParams,
          addOnID,
          spaceOfferingID,
        };
      });
      setOrderParamsError((prev) => ({
        ...prev,
        spaceID: "",
        dateRange: "",
        promoCode: "",
        addOnID: "",
        spaceOfferingID: "",
        quantity: "",
        email: "",
      }));
    },
    []
  );

  const handleChangeDateRange = useCallback((dateRange?: DateTimeValue) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      dateRange,
      addOns: undefined,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      dateRange: "",
    }));
  }, []);

  const mp = whitelistedAddOns.find(
    (mp) =>
      mp.addOnID === orderParams.addOnID || orderParams.addOnID === "daypass"
  );

  const validateOrderParams = useCallback(() => {
    if (!orderParams.spaceID) {
      setOrderParamsError((prev) => ({
        ...prev,
        spaceID: fbt("Please select a location"),
      }));

      return false;
    }

    if (mp?.numberOfEntries === 1) {
      const dateRange = orderParams.dateRange;
      const hasValidRange = isHourlyValue(dateRange)
        ? !!(dateRange.date && dateRange.startTime && dateRange.endTime)
        : !!(dateRange?.startDate && dateRange?.endDate);
      if (!hasValidRange) {
        setOrderParamsError((prev) => ({
          ...prev,
          dateRange: fbt("Please select date range"),
        }));
        return false;
      }
    } else if (!orderParams.addOnID) {
      setOrderParamsError((prev) => ({
        ...prev,
        addOnID: fbt("Please select # of entries"),
      }));

      return false;
    } else if (!orderParams.spaceOfferingID) {
      setOrderParamsError((prev) => ({
        ...prev,
        spaceOfferingID: fbt("Please select # of entries"),
      }));

      return false;
    }

    return true;
  }, [mp, orderParams, fbt]);

  const handleChangePromoCode = useCallback((promoCode: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      promoCode,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      promoCode: "",
    }));
  }, []);

  useEffect(() => {
    if (promoCode) {
      handleChangePromoCode(promoCode);
    }
  }, [promoCode, handleChangePromoCode]);

  const handleChangeEmail = useHandleChangeEmail(
    setOrderParams,
    setOrderParamsError
  );

  const handleChangeAddOns = useCallback((addOns: AddOnOrderParams[]) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      addOns: addOns.length > 0 ? addOns : undefined,
    }));
  }, []);

  let errorText = "";
  if (orderParamsError.spaceID) {
    errorText = orderParamsError.spaceID;
  } else if (orderParamsError.dateRange) {
    errorText = orderParamsError.dateRange;
  } else if (orderParamsError.addOnID) {
    errorText = orderParamsError.addOnID;
  } else if (orderParamsError.spaceOfferingID) {
    errorText = orderParamsError.spaceOfferingID;
  } else if (orderParamsError.quantity) {
    errorText = orderParamsError.quantity;
  }

  return {
    validateOrderParams,
    handleChangeAddOnID,
    handleChangeDateRange,
    handleChangeQuantity,
    handleChangeLocation,
    handleChangePromoCode,
    handleChangeEmail,
    handleChangeCity,
    handleChangeAddOns,
    setOrderParams,
    orderParams,
    orderParamsError,
    errorText,
  };
}

export interface DayPassOrderParamsErrors {
  dateRange: string;
  spaceID: string;
  promoCode: string;
  quantity: string;
  email: string;
}

export function useDayPassOrderParamsHandlers(
  initialDayPassOrderParams?: DayPassOrderParams,
  partnerOfferingId?: string
) {
  const [orderParams, setOrderParams] = useState(
    initialDayPassOrderParams || normalizeDayPassOrderParams({})
  );
  const { fbt } = useI18n();
  const [orderParamsError, setOrderParamsError] =
    useState<DayPassOrderParamsErrors>({
      dateRange: "",
      spaceID: "",
      promoCode: "",
      quantity: "",
      email: "",
    });
  const handleChangeLocation = useCallback((spaceID: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      spaceID,
      dateRange: undefined,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      spaceID: "",
      dateRange: "",
      promoCode: "",
      quantity: "",
      email: "",
    }));
  }, []);

  const handleChangeQuantity = useCallback((quantity: number) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      quantity,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      spaceID: "",
      dateRange: "",
      promoCode: "",
      quantity: "",
      email: "",
    }));
  }, []);

  const handleChangeDayPassDateRange = useCallback(
    (dateRange?: DayPassDateRange) => {
      setOrderParams((prevOrderParams) => ({
        ...prevOrderParams,
        dateRange,
      }));
      setOrderParamsError((prev) => ({
        ...prev,
        dateRange: "",
      }));
    },
    []
  );

  const validateOrderParams = useCallback(() => {
    if (!orderParams.spaceID) {
      setOrderParamsError((prev) => ({
        ...prev,
        spaceID: fbt("Please select a location"),
      }));

      return false;
    }

    if (!orderParams.dateRange?.startDate || !orderParams.dateRange.endDate) {
      setOrderParamsError((prev) => ({
        ...prev,
        dateRange: fbt("Please select date range"),
      }));
      return false;
    }

    return true;
  }, [orderParams, fbt]);

  const handleChangePromoCode = useCallback((promoCode: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      promoCode,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      promoCode: "",
    }));
  }, []);

  const handleChangeEmail = useHandleChangeEmail(
    setOrderParams,
    setOrderParamsError
  );

  let errorText = "";
  if (orderParamsError.spaceID) {
    errorText = orderParamsError.spaceID;
  } else if (orderParamsError.dateRange) {
    errorText = orderParamsError.dateRange;
  } else if (orderParamsError.quantity) {
    errorText = orderParamsError.quantity;
  }

  return {
    validateOrderParams,
    handleChangeQuantity,
    handleChangeDayPassDateRange,
    handleChangeLocation,
    handleChangePromoCode,
    handleChangeEmail,
    orderParams,
    orderParamsError,
    errorText,
    setOrderParamsError,
    setOrderParams,
  };
}

export interface DayPassWithCapacityOrderParamsErrors {
  dateRange: string;
  spaceID: string;
  partnerOfferingLocationID: string;
  promoCode: string;
  capacity: string;
  email: string;
}

export function useDayPassWithCapacityOrderParamsHandlers(
  initialDayPassWithCapacityOrderParams?: DayPassWithCapacityOrderParams
) {
  const [orderParams, setOrderParams] = useState(
    initialDayPassWithCapacityOrderParams ||
      normalizeDayPassWithCapacityOrderParams({})
  );
  const { fbt } = useI18n();
  const [orderParamsError, setOrderParamsError] =
    useState<DayPassWithCapacityOrderParamsErrors>({
      dateRange: "",
      partnerOfferingLocationID: "",
      promoCode: "",
      spaceID: "",
      capacity: "",
      email: "",
    });
  const handleChangeLocation = useCallback(
    (partnerOfferingLocationID: string) => {
      setOrderParams((prevOrderParams) => ({
        ...prevOrderParams,
        partnerOfferingLocationID,
        dateRange: undefined,
      }));
      setOrderParamsError((prev) => ({
        ...prev,
        partnerOfferingLocationID: "",
        dateRange: "",
        promoCode: "",
        capacity: "",
        email: "",
      }));
    },
    []
  );

  const handleChangeCapacity = useCallback((capacity: number) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      dateRange: undefined,
      capacity,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      spaceID: "",
      locationID: "",
      dateRange: "",
      promoCode: "",
      capacity: "",
      email: "",
    }));
  }, []);

  const handleChangeDayPassDateRange = useCallback(
    (dateRange?: DayPassDateRange) => {
      setOrderParams((prevOrderParams) => ({
        ...prevOrderParams,
        dateRange,
      }));
      setOrderParamsError((prev) => ({
        ...prev,
        dateRange: "",
      }));
    },
    []
  );

  const validateOrderParams = useCallback(() => {
    if (!orderParams.partnerOfferingLocationID) {
      setOrderParamsError((prev) => ({
        ...prev,
        partnerOfferingLocationID: fbt("Please select a location"),
      }));

      return false;
    }

    if (!orderParams.dateRange?.startDate || !orderParams.dateRange.endDate) {
      setOrderParamsError((prev) => ({
        ...prev,
        dateRange: fbt("Please select date range"),
      }));
      return false;
    }

    return true;
  }, [orderParams, fbt]);

  const handleChangePromoCode = useCallback((promoCode: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      promoCode,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      promoCode: "",
    }));
  }, []);

  const handleChangeEmail = useHandleChangeEmail(
    setOrderParams,
    setOrderParamsError
  );

  const handleChangeSpace = useCallback((spaceID: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      spaceID,
    }));
  }, []);

  let errorText = "";
  if (orderParamsError.partnerOfferingLocationID) {
    errorText = orderParamsError.partnerOfferingLocationID;
  } else if (orderParamsError.dateRange) {
    errorText = orderParamsError.dateRange;
  } else if (orderParamsError.capacity) {
    errorText = orderParamsError.capacity;
  }

  return {
    validateOrderParams,
    handleChangeCapacity,
    handleChangeDayPassDateRange,
    handleChangeLocation,
    handleChangeSpace,
    handleChangePromoCode,
    handleChangeEmail,
    orderParams,
    orderParamsError,
    errorText,
    setOrderParams,
    setOrderParamsError,
  };
}

export interface DailyOfficeOrderParamsErrors {
  dateRange: string;
  spaceID: string;
  partnerOfferingLocationID: string;
  addOnID: string;
  spaceOfferingID: string;
  promoCode: string;
  capacity: string;
  email: string;
}

export function useDailyOfficeOrderParamsHandlers(
  whitelistedAddOns: SpaceOffering[],
  initialDailyOfficeOrderParams?: DailyOfficeOrderParams
) {
  const { promoCode } = useQueryString<{ promoCode?: string }>();

  const [orderParams, setOrderParams] = useState(
    initialDailyOfficeOrderParams || normalizeDailyOfficeOrderParams({})
  );
  const { fbt } = useI18n();
  const [orderParamsError, setOrderParamsError] =
    useState<DailyOfficeOrderParamsErrors>({
      dateRange: "",
      partnerOfferingLocationID: "",
      promoCode: "",
      spaceID: "",
      capacity: "",
      addOnID: "",
      spaceOfferingID: "",
      email: "",
    });

  const handleChangeCity = useCallback((city: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      city,
      partnerOfferingLocationID: undefined,
      dateRange: undefined,
      capacity: undefined,
      addOnID: undefined,
      spaceOfferingID: undefined,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      partnerOfferingLocationID: "",
      dateRange: "",
      promoCode: "",
      addOnID: "",
      spaceOfferingID: "",
      capacity: "",
      email: "",
    }));
  }, []);

  const handleChangeLocation = useCallback(
    (partnerOfferingLocationID: string) => {
      setOrderParams((prevOrderParams) => ({
        ...prevOrderParams,
        partnerOfferingLocationID,
        dateRange: undefined,
        capacity: undefined,
        addOnID: undefined,
        spaceOfferingID: undefined,
      }));
      setOrderParamsError((prev) => ({
        ...prev,
        partnerOfferingLocationID: "",
        dateRange: "",
        promoCode: "",
        addOnID: "",
        spaceOfferingID: "",
        capacity: "",
        email: "",
      }));
    },
    []
  );

  const handleChangeCapacity = useCallback((capacity: number) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      capacity,
      dateRange: undefined,
      addOnID: undefined,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      spaceID: "",
      locationID: "",
      addOnID: "",
      spaceOfferingID: "",
      dateRange: "",
      promoCode: "",
      capacity: "",
      email: "",
    }));
  }, []);

  const handleChangeAddOnID = useCallback(
    (addOnID: string, spaceOfferingID: string) => {
      setOrderParams((prevOrderParams) => {
        if (addOnID) {
          return {
            ...prevOrderParams,
            addOnID,
            spaceOfferingID,
            dateRange: undefined,
            quantity: 1,
          };
        }

        return {
          ...prevOrderParams,
          addOnID,
          spaceOfferingID,
        };
      });
      setOrderParamsError((prev) => ({
        ...prev,
        spaceID: "",
        dateRange: "",
        promoCode: "",
        addOnID: "",
        spaceOfferingID: "",
        quantity: "",
        email: "",
      }));
    },
    []
  );

  const handleChangeDateRange = useCallback((dateRange?: DateTimeValue) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      dateRange,
      addOns: undefined,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      dateRange: "",
    }));
  }, []);

  const mp = whitelistedAddOns.find(
    (mp) =>
      mp.addOnID === orderParams.addOnID || orderParams.addOnID === "dayoffice"
  );

  const validateOrderParams = useCallback(() => {
    if (!orderParams.partnerOfferingLocationID) {
      setOrderParamsError((prev) => ({
        ...prev,
        partnerOfferingLocationID: fbt("Please select a location"),
      }));

      return false;
    }

    if (mp?.numberOfEntries === 1) {
      const dateRange = orderParams.dateRange;
      const hasHourly =
        isHourlyValue(dateRange) &&
        !!dateRange.date &&
        !!dateRange.startTime &&
        !!dateRange.endTime;
      const hasDaily =
        !!dateRange &&
        !isHourlyValue(dateRange) &&
        !!dateRange.startDate &&
        !!dateRange.endDate;
      if (!hasHourly && !hasDaily) {
        setOrderParamsError((prev) => ({
          ...prev,
          dateRange: fbt("Please select date range"),
        }));
        return false;
      }
    } else if (!orderParams.addOnID) {
      setOrderParamsError((prev) => ({
        ...prev,
        addOnID: fbt("Please select quantity"),
      }));

      return false;
    } else if (!orderParams.spaceOfferingID) {
      setOrderParamsError((prev) => ({
        ...prev,
        spaceOfferingID: fbt("Please select quantity"),
      }));

      return false;
    }

    return true;
  }, [
    orderParams.partnerOfferingLocationID,
    orderParams.addOnID,
    orderParams.spaceOfferingID,
    orderParams.dateRange,
    mp,
    fbt,
  ]);

  const handleChangePromoCode = useCallback((promoCode: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      promoCode,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      promoCode: "",
    }));
  }, []);

  useEffect(() => {
    if (promoCode) {
      handleChangePromoCode(promoCode);
    }
  }, [promoCode, handleChangePromoCode]);

  const handleChangeEmail = useHandleChangeEmail(
    setOrderParams,
    setOrderParamsError
  );

  const handleChangeSpace = useCallback((spaceID: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      spaceID,
    }));
  }, []);

  const handleChangeAddOns = useCallback((value: AddOnOrderParams[]) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      addOns: value,
    }));
  }, []);

  let errorText = "";
  if (orderParamsError.partnerOfferingLocationID) {
    errorText = orderParamsError.partnerOfferingLocationID;
  } else if (orderParamsError.dateRange) {
    errorText = orderParamsError.dateRange;
  } else if (orderParamsError.capacity) {
    errorText = orderParamsError.capacity;
  } else if (orderParamsError.addOnID) {
    errorText = orderParamsError.addOnID;
  } else if (orderParamsError.spaceOfferingID) {
    errorText = orderParamsError.spaceOfferingID;
  }

  return {
    validateOrderParams,
    handleChangeAddOnID,
    handleChangeCapacity,
    handleChangeDateRange,
    handleChangeLocation,
    handleChangeSpace,
    handleChangePromoCode,
    handleChangeEmail,
    handleChangeCity,
    handleChangeAddOns,
    setOrderParams,
    orderParams,
    orderParamsError,
    errorText,
  };
}

export interface MeetingRoomOrderParamsErrors {
  dateRange: string;
  spaceID: string;
  promoCode: string;
  email: string;
}

export function useMeetingRoomOrderParamsHandlers(
  initialMeetingRoomOrderParams?: MeetingRoomOrderParams
) {
  const { promoCode } = useQueryString<{ promoCode?: string }>();

  const [orderParams, setOrderParams] = useState(
    initialMeetingRoomOrderParams || normalizeMeetingRoomOrderParams({})
  );
  const { fbt } = useI18n();
  const [orderParamsError, setOrderParamsError] =
    useState<MeetingRoomOrderParamsErrors>({
      dateRange: "",
      spaceID: "",
      promoCode: "",
      email: "",
    });

  const handleChangeLocation = useCallback((spaceID: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      spaceID,
      dateRange: undefined,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      spaceID: "",
      dateRange: "",
      promoCode: "",
      quantity: "",
      email: "",
    }));
  }, []);

  const handleChangeAttendees = useCallback((value: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      attendees: parseInt(value),
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      attendees: "",
    }));
  }, []);

  const handleChangeLayout = useCallback((layoutID: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      layoutID,
    }));
  }, []);

  // The repeat rule is committed together with the dates it repeats, in one
  // state write: two writes would quote the old rule against the new dates in
  // between. Omitting it clears it — a picker saved with Repeat off is not
  // recurring. Add-ons are dropped when the dates move (they were picked for
  // the old dates) or when the booking becomes recurring (the quote refuses
  // them); a rule change alone leaves them be.
  const handleChangeDateRange = useCallback(
    (dateRange?: DateTimeValue, recurrence?: RecurrenceRule) => {
      setOrderParams((prevOrderParams) => {
        const sameDates =
          JSON.stringify(prevOrderParams.dateRange ?? null) ===
          JSON.stringify(dateRange ?? null);
        return {
          ...prevOrderParams,
          dateRange,
          recurrence,
          type: "MeetingRoom",
          addOns: sameDates && !recurrence ? prevOrderParams.addOns : undefined,
        };
      });
      setOrderParamsError((prev) => ({
        ...prev,
        dateRange: "",
      }));
    },
    []
  );

  const validateOrderParams = useCallback(() => {
    if (!orderParams.spaceID) {
      setOrderParamsError((prev) => ({
        ...prev,
        spaceID: fbt("Please select a location"),
      }));

      return false;
    }

    if (isHourlyValue(orderParams.dateRange)) {
      if (!orderParams.dateRange?.date) {
        setOrderParamsError((prev) => ({
          ...prev,
          dateRange: fbt("Please select date"),
        }));
        return false;
      }
      if (!orderParams.dateRange?.startTime) {
        setOrderParamsError((prev) => ({
          ...prev,
          dateRange: fbt("Please select time"),
        }));
        return false;
      }
      if (!orderParams.dateRange?.endTime) {
        setOrderParamsError((prev) => ({
          ...prev,
          dateRange: fbt("Please select duration"),
        }));
        return false;
      }
    } else {
      if (!orderParams.dateRange?.startDate) {
        setOrderParamsError((prev) => ({
          ...prev,
          dateRange: fbt("Please select start date"),
        }));
        return false;
      }

      if (!orderParams.dateRange?.endDate) {
        setOrderParamsError((prev) => ({
          ...prev,
          dateRange: fbt("Please select end date"),
        }));
        return false;
      }
    }

    return true;
  }, [orderParams, fbt]);

  const handleChangePromoCode = useCallback((promoCode: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      promoCode,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      promoCode: "",
    }));
  }, []);

  useEffect(() => {
    if (promoCode) {
      handleChangePromoCode(promoCode);
    }
  }, [promoCode, handleChangePromoCode]);

  const handleChangeEmail = useHandleChangeEmail(
    setOrderParams,
    setOrderParamsError
  );

  const handleChangeAddOns = useCallback((addOns: AddOnOrderParams[]) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      addOns: addOns.length > 0 ? addOns : undefined,
    }));
  }, []);

  let errorText = "";
  if (orderParamsError.spaceID) {
    errorText = orderParamsError.spaceID;
  } else if (orderParamsError.dateRange) {
    errorText = orderParamsError.dateRange;
  }

  return {
    validateOrderParams,
    handleChangeDateRange,
    handleChangeLocation,
    handleChangeAttendees,
    handleChangeLayout,
    handleChangePromoCode,
    handleChangeEmail,
    handleChangeAddOns,
    setOrderParams,
    orderParams,
    orderParamsError,
    errorText,
    setOrderParamsError,
  };
}

export function useEventSpaceOrderParamsHandlers(
  initialParams: EventSpaceOrderParams
) {
  const [orderParams, setOrderParams] =
    useState<EventSpaceOrderParams>(initialParams);

  const handleChangeEventType = (
    id?: string,
    otherEventDescription?: string
  ) => {
    setOrderParams((prev) => ({
      ...prev,
      eventTypeID: id,
      eventTypeDescription: otherEventDescription,
    }));
  };

  const handleChangeAttendees = (value?: number) => {
    setOrderParams((prev) => ({ ...prev, attendees: value }));
  };

  const handleChangeLayout = (id?: string) => {
    setOrderParams((prev) => ({ ...prev, layoutID: id }));
  };

  const handleChangeDateRange = (dateRange?: DateTimeValue) => {
    setOrderParams((prev) => ({
      ...prev,
      dateRange,
      addOns: undefined,
    }));
  };

  const handleChangePromoCode = useCallback((promoCode: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      promoCode,
    }));
  }, []);

  const handleChangeEmail = useCallback((email?: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      email,
    }));
  }, []);

  const handleChangeAddOns = useCallback((addOns: AddOnOrderParams[]) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      addOns: addOns.length > 0 ? addOns : undefined,
    }));
  }, []);

  const validateOrderParams = useCallback(() => {
    if (!orderParams.spaceID) {
      return false;
    }

    if (isHourlyValue(orderParams.dateRange)) {
      if (!orderParams.dateRange?.date) {
        return false;
      }
      if (!orderParams.dateRange?.startTime) {
        return false;
      }
      if (!orderParams.dateRange?.endTime) {
        return false;
      }
    } else {
      if (!orderParams.dateRange?.startDate) {
        return false;
      }

      if (!orderParams.dateRange?.endDate) {
        return false;
      }
    }

    return true;
  }, [orderParams]);

  return {
    orderParams,
    setOrderParams,
    handleChangeAttendees,
    handleChangeEventType,
    handleChangeDateRange,
    handleChangeLayout,
    handleChangePromoCode,
    handleChangeEmail,
    validateOrderParams,
    handleChangeAddOns,
  };
}

export interface MultiPassOrderParamsErrors {
  spaceID: string;
  addOnID: string;
  spaceOfferingID: string;
  promoCode: string;
  email: string;
}

export function useMultiPassOrderParamsHandlers(
  initialMultiPassOrderParams?: MultiPassOrderParams
) {
  const [orderParams, setOrderParams] = useState(
    initialMultiPassOrderParams || normalizeMultiPassOrderParams({})
  );
  const { fbt } = useI18n();
  const [orderParamsError, setOrderParamsError] =
    useState<MultiPassOrderParamsErrors>({
      addOnID: "",
      spaceOfferingID: "",
      spaceID: "",
      promoCode: "",
      email: "",
    });
  const handleChangeLocation = useCallback((spaceID: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      spaceID,
      addOnID: undefined,
      spaceOfferingID: undefined,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      spaceID: "",
      addOnID: "",
      spaceOfferingID: "",
      promoCode: "",
      email: "",
    }));
  }, []);

  const handleChangeMultiPass = useCallback(
    (multiPassID: string, spaceOfferingID: string) => {
      setOrderParams((prevOrderParams) => ({
        ...prevOrderParams,
        addOnID: multiPassID,
        spaceOfferingID: spaceOfferingID,
      }));
      setOrderParamsError((prev) => ({
        ...prev,
        addOnID: "",
        spaceOfferingID: "",
      }));
    },
    []
  );

  const validateOrderParams = useCallback(() => {
    if (!orderParams.spaceID) {
      setOrderParamsError((prev) => ({
        ...prev,
        spaceID: fbt("Please select a location"),
      }));

      return false;
    }
    if (!orderParams.addOnID) {
      setOrderParamsError((prev) => ({
        ...prev,
        addOnID: fbt("Please select # of entries"),
      }));
      return false;
    }

    if (!orderParams.spaceOfferingID) {
      setOrderParamsError((prev) => ({
        ...prev,
        spaceOfferingID: fbt("Please select # of entries"),
      }));
      return false;
    }

    return true;
  }, [orderParams, fbt]);

  const handleChangePromoCode = useCallback((promoCode: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      promoCode,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      promoCode: "",
    }));
  }, []);

  const handleChangeEmail = useHandleChangeEmail(
    setOrderParams,
    setOrderParamsError
  );

  let errorText = "";
  if (orderParamsError.spaceID) {
    errorText = orderParamsError.spaceID;
  } else if (orderParamsError.addOnID) {
    errorText = orderParamsError.addOnID;
  } else if (orderParamsError.spaceOfferingID) {
    errorText = orderParamsError.spaceOfferingID;
  }

  return {
    validateOrderParams,
    handleChangeLocation,
    handleChangeMultiPass,
    handleChangePromoCode,
    handleChangeEmail,
    orderParams,
    orderParamsError,
    errorText,
    setOrderParams,
    setOrderParamsError,
  };
}

export interface MultiDayOfficeOrderParamsErrors {
  partnerOfferingLocationID: string;
  addOnID: string;
  spaceOfferingID: string;
  capacity: string;
  promoCode: string;
  email: string;
}

export function useMultiDayOfficeOrderParamsHandlers(
  initialMultiDayOfficeOrderParams?: MultiDayOfficeOrderParams
) {
  const [orderParams, setOrderParams] = useState(
    initialMultiDayOfficeOrderParams || normalizeMultiDayOfficeOrderParams({})
  );

  const { fbt } = useI18n();
  const [orderParamsError, setOrderParamsError] =
    useState<MultiDayOfficeOrderParamsErrors>({
      addOnID: "",
      spaceOfferingID: "",
      promoCode: "",
      partnerOfferingLocationID: "",
      capacity: "",
      email: "",
    });

  const handleChangeLocation = useCallback(
    (partnerOfferingLocationID: string) => {
      setOrderParams((prevOrderParams) => ({
        ...prevOrderParams,
        partnerOfferingLocationID: partnerOfferingLocationID,
        dateRange: undefined,
        addOnID: undefined,
        spaceOfferingID: undefined,
      }));
      setOrderParamsError((prev) => ({
        ...prev,
        addOnID: "",
        spaceOfferingID: "",
        promoCode: "",
        partnerOfferingLocationID: "",
        capacity: "",
        email: "",
      }));
    },
    []
  );

  const handleChangeMultiPass = useCallback(
    (multiPassID: string, spaceOfferingID: string) => {
      setOrderParams((prevOrderParams) => ({
        ...prevOrderParams,
        addOnID: multiPassID,
        spaceOfferingID: spaceOfferingID,
      }));
      setOrderParamsError((prev) => ({
        ...prev,
        addOnID: "",
        spaceOfferingID: "",
      }));
    },
    []
  );

  const handleChangePromoCode = useCallback((promoCode: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      promoCode,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      promoCode: "",
    }));
  }, []);

  const handleChangeEmail = useHandleChangeEmail(
    setOrderParams,
    setOrderParamsError
  );

  const handleChangeCapacity = useCallback((capacity: number) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      capacity: capacity,
      addOnID: undefined,
      spaceOfferingID: undefined,
    }));
  }, []);

  const handleChangeSpace = useCallback((spaceID: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      spaceID,
    }));
  }, []);

  const validateOrderParams = useCallback(() => {
    if (!orderParams.partnerOfferingLocationID && !orderParams.spaceID) {
      setOrderParamsError((prev) => ({
        ...prev,
        partnerOfferingLocationID: fbt("Please select a location"),
      }));

      return false;
    }
    if (!orderParams.capacity) {
      setOrderParamsError((prev) => ({
        ...prev,
        capacity: fbt("Please select a capacity"),
      }));

      return false;
    }
    if (!orderParams.addOnID) {
      setOrderParamsError((prev) => ({
        ...prev,
        addOnID: fbt("Please select # of entries"),
      }));
      return false;
    }
    if (!orderParams.spaceOfferingID) {
      setOrderParamsError((prev) => ({
        ...prev,
        spaceOfferingID: fbt("Please select # of entries"),
      }));
      return false;
    }

    return true;
  }, [orderParams, fbt]);

  let errorText = "";
  if (orderParamsError.partnerOfferingLocationID) {
    errorText = orderParamsError.partnerOfferingLocationID;
  } else if (orderParamsError.addOnID) {
    errorText = orderParamsError.addOnID;
  } else if (orderParamsError.spaceOfferingID) {
    errorText = orderParamsError.spaceOfferingID;
  } else if (orderParamsError.capacity) {
    errorText = orderParamsError.capacity;
  }

  return {
    validateOrderParams,
    handleChangeLocation,
    handleChangeMultiPass,
    handleChangePromoCode,
    handleChangeCapacity,
    handleChangeSpace,
    handleChangeEmail,
    orderParams,
    orderParamsError,
    errorText,
    setOrderParams,
    setOrderParamsError,
  };
}

export function useMembershipOrderParamsHandlers(
  membershipV2FetureFlag: boolean,
  initialMembershipOrderParams?: MembershipOrderParams
) {
  const { promoCode } = useQueryString<{ promoCode?: string }>();

  const [orderParams, setOrderParams] = useState(
    initialMembershipOrderParams ||
      normalizeMembershipOrderParams({}, membershipV2FetureFlag)
  );
  const { fbt } = useI18n();
  const [orderParamsError, setOrderParamsError] =
    useState<MembershipOrderParamsErrors>({
      promoCode: "",
      email: "",
      locationID: "",
      subscriptionStartDate: "",
    });

  const handleChangeCity = useCallback((city: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      city,
      locationID: undefined,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      promoCode: "",
      email: "",
      locationID: "",
    }));
  }, []);

  const handleChangeLocation = useCallback((locationID: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      locationID,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      promoCode: "",
      email: "",
      locationID: "",
    }));
  }, []);

  const handleChangeStartDate = useCallback(
    (subscriptionStartDate?: string) => {
      setOrderParams((prevOrderParams) => ({
        ...prevOrderParams,
        subscriptionStartDate,
      }));
      setOrderParamsError((prev) => ({
        ...prev,
        subscriptionStartDate: "",
      }));
    },
    []
  );

  const validateOrderParams = useCallback(() => {
    if (!orderParams.locationID) {
      setOrderParamsError((prev) => ({
        ...prev,
        locationID: fbt("Please select a location"),
      }));

      return false;
    }

    return true;
  }, [orderParams, fbt]);

  const handleChangePromoCode = useCallback((promoCode: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      promoCode,
    }));
    setOrderParamsError((prev) => ({
      ...prev,
      promoCode: "",
    }));
  }, []);

  useEffect(() => {
    if (promoCode) {
      handleChangePromoCode(promoCode);
    }
  }, [promoCode, handleChangePromoCode]);

  const handleChangeEmail = useHandleChangeEmail(
    setOrderParams,
    setOrderParamsError
  );

  const handleChangeMembershipId = useCallback((membershipID: string) => {
    setOrderParams((prevOrderParams) => ({
      ...prevOrderParams,
      membershipID,
    }));
  }, []);

  let errorText = "";
  if (orderParamsError.locationID) {
    errorText = orderParamsError.locationID;
  }
  return {
    validateOrderParams,
    handleChangeLocation,
    handleChangePromoCode,
    handleChangeEmail,
    handleChangeCity,
    setOrderParams,
    orderParams,
    handleChangeStartDate,
    orderParamsError,
    errorText,
    handleChangeMembershipId,
  };
}
